import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight, Phone, ChevronLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const MemberLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotPhone, setForgotPhone] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      localStorage.setItem("memberToken", response.data.token);
      localStorage.setItem("memberData", JSON.stringify(response.data.member));
      const memberName = response.data.member.first_name || response.data.member.name || "Champion";
      toast.success(`Welcome back, ${memberName}!`);
      if (!response.data.member.is_admin && window.gtag) {
        window.gtag('event', 'login', { method: 'website' });
      }
      if (response.data.member.is_admin) {
        localStorage.setItem("adminToken", response.data.token);
        navigate("/admin");
      } else {
        const redirectTo = sessionStorage.getItem("nbc_redirect_after_login");
        sessionStorage.removeItem("nbc_redirect_after_login");
        navigate(redirectTo || "/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    if (!forgotPhone.trim()) return toast.error("Please enter your phone number");
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { phone: forgotPhone });
      setForgotSent(true);
      setResendCooldown(60);
    } catch {
      // Still show success — don't reveal if number exists
      setForgotSent(true);
      setResendCooldown(60);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { phone: forgotPhone });
      toast.success("Link resent!");
      setResendCooldown(60);
    } catch {
      toast.success("Link resent!");
      setResendCooldown(60);
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img
            src="/nbc_logo.jpg"
            alt="NBC"
            className="h-16 mx-auto mb-6"
          />
          <h1 className="text-3xl font-bold uppercase">
            {showForgot ? "Reset Password" : "Member Login"}
          </h1>
          <p className="text-white/60 mt-2">
            {showForgot ? "We'll text you a secure reset link" : "Access your member dashboard"}
          </p>
        </div>

        {/* ── LOGIN FORM ── */}
        {!showForgot && (
          <form onSubmit={handleLogin} className="bg-[#0A0A0A] border border-white/10 p-8 space-y-6">
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
              <Input
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-[#050505] border-white/20 h-12 pl-12 text-white placeholder:text-white/40"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  type="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="bg-[#050505] border-white/20 h-12 pl-12 text-white placeholder:text-white/40"
                  data-testid="login-password-input"
                />
              </div>
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => setShowForgot(true)}
                  className="text-xs text-white/40 hover:text-[#FFB612] transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide h-12"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing In..." : "Sign In"}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </form>
        )}

        {/* ── FORGOT PASSWORD FORM ── */}
        {showForgot && (
          <div className="bg-[#0A0A0A] border border-white/10 p-8">
            {!forgotSent ? (
              <form onSubmit={handleForgotSubmit} className="space-y-6">
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    type="tel"
                    placeholder="Your Phone Number"
                    value={forgotPhone}
                    onChange={(e) => setForgotPhone(e.target.value)}
                    className="bg-[#050505] border-white/20 h-12 pl-12 text-white placeholder:text-white/40"
                    autoFocus
                    data-testid="forgot-phone-input"
                  />
                </div>
                <p className="text-xs text-white/40">
                  Enter the phone number on your account. If it exists, you'll receive a reset link via SMS.
                </p>
                <Button
                  type="submit"
                  disabled={forgotLoading}
                  className="w-full bg-[#FFB612] text-black hover:bg-[#E5A00D] font-bold uppercase tracking-wide h-12"
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? "Sending..." : "Send Reset Link"}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowForgot(false)}
                  className="w-full text-sm text-white/40 hover:text-white flex items-center justify-center gap-1 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Back to Login
                </button>
              </form>
            ) : (
              <div className="text-center space-y-6">
                <div className="w-16 h-16 bg-green-500/10 border border-green-500/30 flex items-center justify-center mx-auto">
                  <Phone className="w-8 h-8 text-green-400" />
                </div>
                <div>
                  <p className="font-bold text-lg mb-2">Check Your Phone</p>
                  <p className="text-white/60 text-sm">
                    If <span className="text-white">••••••{forgotPhone.replace(/\D/g, "").slice(-4)}</span> is registered, a reset link was sent. It expires in <span className="text-[#FFB612]">15 minutes</span>.
                  </p>
                </div>
                <Button
                  onClick={handleResend}
                  disabled={resendCooldown > 0 || forgotLoading}
                  variant="outline"
                  className="w-full border-white/20 text-white/70"
                  data-testid="resend-sms-btn"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend Link"}
                </Button>
                <button
                  type="button"
                  onClick={() => { setShowForgot(false); setForgotSent(false); setForgotPhone(""); }}
                  className="w-full text-sm text-white/40 hover:text-white flex items-center justify-center gap-1 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Back to Login
                </button>
              </div>
            )}
          </div>
        )}

        <div className="text-center mt-6 space-y-4">
          {!showForgot && (
            <p className="text-white/60">
              Don't have an account?{" "}
              <Link to="/register" className="text-[#FFB612] hover:underline">Register Now</Link>
            </p>
          )}
          <p className="text-white/40 text-sm">
            <Link to="/" className="hover:text-[#FFB612]">← Back to Website</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
